import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLatest } from 'ahooks';

/**
 * 互斥下拉/面板状态机：同一时刻最多一个 key active，纯状态机不带任何全局 listener。
 * 关闭统一由各 popover 自带的 outsideClick / 显式调用 close 处理。
 */

export interface IExclusiveHandle {
  /** 当前 popover 是否是 activeKey */
  isOpen: boolean;
  /** 强制把 active 切到自己（互斥关掉其它） */
  open: () => void;
  /** 仅当自己是 active 时清空 active；否则 no-op */
  close: () => void;
  /** 标准受控适配器：true → open()，false → close() */
  onOpenChange: (next: boolean) => void;
}

export interface IExclusivePopoversApi<K extends string> {
  /** 当前 active 的 key，null 表示全部关闭 */
  activeKey: K | null;
  /** 是否有任意 popover 处于 active */
  isAnyOpen: boolean;
  /** 关闭所有 */
  closeAll: () => void;
  /** 取某个 key 的受控适配器 */
  get: (key: K) => IExclusiveHandle;
}

export const useExclusivePopovers = <K extends string>(): IExclusivePopoversApi<K> => {
  const [activeKey, setActiveKey] = useState<K | null>(null);

  const get = useCallback(
    (key: K): IExclusiveHandle => ({
      isOpen: activeKey === key,
      open: () => setActiveKey(key),
      close: () => setActiveKey((curr) => (curr === key ? null : curr)),
      onOpenChange: (next: boolean) => {
        if (next) {
          setActiveKey(key);
        } else {
          setActiveKey((curr) => (curr === key ? null : curr));
        }
      },
    }),
    [activeKey],
  );

  const closeAll = useCallback(() => setActiveKey(null), []);

  return useMemo(
    () => ({
      activeKey,
      isAnyOpen: activeKey !== null,
      closeAll,
      get,
    }),
    [activeKey, closeAll, get],
  );
};

/**
 * 把外部 host hook 受控的 open 接入互斥状态机：
 * - external.open=true → 切到 key
 * - 互斥切走该 key → 通知外部 onChange(false)，ref 防回环
 * - disableMutexClose=true 时仅打开仍互斥（抢 activeKey），但不再把自己关掉，
 *   用于 # 触发等 textarea 焦点场景，避免 hover Agent/Skill 时误关 chart 浮窗
 */
export const useExternalPopoverSync = <K extends string>(
  api: IExclusivePopoversApi<K>,
  key: K,
  external?: { open?: boolean; onChange?: (open: boolean) => void; } | null,
  options?: { disableMutexClose?: boolean; },
): void => {
  const isMutexClosingRef = useRef(false);
  const externalOpen = external?.open;
  const externalOnChange = external?.onChange;
  const { activeKey, get } = api;
  /** 用 ref 读最新豁免开关，避免把它加入 effect 依赖触发回环 */
  const disableMutexCloseLatest = useLatest(options?.disableMutexClose ?? false);

  /** external → internal：依赖仅 externalOpen 避免回环 */
  useEffect(() => {
    if (!external) return;
    if (externalOpen) {
      if (activeKey !== key) {
        get(key).open();
      }
    } else if (activeKey === key && !isMutexClosingRef.current) {
      get(key).close();
    }
    isMutexClosingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOpen]);

  /** internal → external：互斥切走时通知外部关闭（disableMutexClose 时豁免） */
  useEffect(() => {
    if (!external) return;
    if (activeKey !== key && externalOpen) {
      if (disableMutexCloseLatest.current) return;
      isMutexClosingRef.current = true;
      externalOnChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);
};

export default useExclusivePopovers;
